// src/pages/dashboard/Employees.js
// CORRECTED VERSION - Fixed all issues + Added NIC column + NIC label

import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Input, 
  message, 
  Popconfirm,
  Modal,
  Form,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
  Card,
  Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined,
  UserOutlined,
  DollarOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { supabase } from '../../services/supabase';
import dayjs from 'dayjs';
import { departmentService } from '../../services/departmentService';
import { Page, PageHeader, PersonCell, StatusPill, formatRs } from '../../components/ui';

const STATUS_TONES = { active: 'positive', inactive: 'neutral', terminated: 'danger' };

const Employees = () => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [form] = Form.useForm();
  const [companyId, setCompanyId] = useState(null);
  const [salaryType, setSalaryType] = useState('hourly');
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    fetchCompanyId();
  }, []);

  const fetchCompanyId = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: companyData, error } = await supabase
        .from('companies')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setCompanyId(companyData.id);
      fetchEmployees(companyData.id);
      fetchDepartments(companyData.id);
    } catch (error) {
      message.error('Error fetching company data');
    }
  };

  const fetchEmployees = async (company_id) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('employees')
        .select('*, monthly_overtime_threshold')
        .eq('company_id', company_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      message.error('Error fetching employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async (company_id) => {
    try {
      const data = await departmentService.getDepartments(company_id);
      setDepartments(data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      message.error('Failed to fetch departments');
    }
  };

  const handleAddNew = () => {
    setEditingEmployee(null);
    form.resetFields();
    form.setFieldsValue({
      status: 'active',
      hire_date: dayjs(),
      salary_type: 'hourly',
      hourly_rate: 0,
      weekly_salary: 0,
      monthly_salary: 0,
      break_minutes: 60,
      monthly_overtime_threshold: null // Uses company default if not set
    });
    setSalaryType('hourly');
    setIsModalVisible(true);
  };

  const validateEmployeeId = async (_, value) => {
    if (!value) return Promise.reject('Please input employee ID');
    
    if (editingEmployee && editingEmployee.fingerprint_id === value) {
      return Promise.resolve();
    }
  
    const { data } = await supabase
      .from('employees')
      .select('id')
      .eq('fingerprint_id', value)
      .eq('company_id', companyId)
      .single();
  
    if (data) {
      return Promise.reject('Employee ID already exists');
    }
  
    return Promise.resolve();
  };

  const handleAddEdit = async (values) => {
    try {
      setLoading(true);
      
      const employeeData = {
        ...values,
        hire_date: values.hire_date.format('YYYY-MM-DD'),
        break_minutes: values.break_minutes || 60,
        company_id: companyId
      };

      if (editingEmployee) {
        const { error } = await supabase
          .from('employees')
          .update(employeeData)
          .eq('id', editingEmployee.id)
          .eq('company_id', companyId);

        if (error) throw error;
        message.success('Employee updated successfully');
      } else {
        const { error } = await supabase
          .from('employees')
          .insert([employeeData]);

        if (error) throw error;
        message.success('Employee added successfully');
      }

      setIsModalVisible(false);
      setEditingEmployee(null);
      form.resetFields();
      fetchEmployees(companyId);
    } catch (error) {
      message.error('Error saving employee');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const { error } = await supabase
        .from('employees')
        .delete()
        .eq('id', id)
        .eq('company_id', companyId);

      if (error) throw error;
      message.success('Employee deleted successfully');
      fetchEmployees(companyId);
    } catch (error) {
      message.error('Error deleting employee');
    }
  };

  const handleSalaryTypeChange = (value) => {
    setSalaryType(value);
    
    if (value === 'hourly') {
      form.setFieldsValue({
        weekly_salary: null,
        monthly_salary: null
      });
    } else if (value === 'weekly') {
      form.setFieldsValue({
        hourly_rate: null,
        monthly_salary: null
      });
    } else if (value === 'monthly') {
      form.setFieldsValue({
        hourly_rate: null,
        weekly_salary: null
      });
    }
  };

  const columns = [
    {
      title: 'Employee',
      dataIndex: 'first_name',
      key: 'name',
      width: 260,
      render: (_, record) => (
        <PersonCell
          name={`${record.first_name} ${record.last_name}`}
          sub={`ID ${record.fingerprint_id || '-'}${record.employee_number ? ` · NIC ${record.employee_number}` : ''}`}
        />
      ),
      sorter: (a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`),
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (department) => (department ? <span className="chip">{department}</span> : <span className="text-muted">-</span>),
      sorter: (a, b) => (a.department || '').localeCompare(b.department || ''),
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: 'Break',
      dataIndex: 'break_minutes',
      key: 'break_minutes',
      width: 90,
      render: (minutes) => <span className="num">{minutes || 60} min</span>,
    },
    {
      title: 'OT threshold',
      dataIndex: 'monthly_overtime_threshold',
      key: 'monthly_overtime_threshold',
      width: 130,
      render: (threshold) => (
        threshold ? (
          <span className="num">{threshold} h/month</span>
        ) : (
          <span className="text-muted">Default</span>
        )
      ),
    },
    {
      title: 'Pay',
      key: 'salary',
      align: 'right',
      render: (_, record) => {
        const amounts = {
          hourly: [record.hourly_rate, 'Hourly', 2],
          weekly: [record.weekly_salary, 'Weekly', 0],
          monthly: [record.monthly_salary, 'Monthly', 0],
        };
        const entry = amounts[record.salary_type];
        if (!entry) return <span className="text-muted">-</span>;
        return (
          <span className="stack-cell" style={{ alignItems: 'flex-end' }}>
            <span className="num num--strong">{formatRs(entry[0], entry[2])}</span>
            <span className="stack-cell__sub">{entry[1]}</span>
          </span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <StatusPill tone={STATUS_TONES[status] || 'neutral'}>
          {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
        </StatusPill>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      align: 'right',
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title="Edit employee">
            <Button
              type="text"
              className="icon-action"
              data-tone="primary"
              aria-label="Edit employee"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingEmployee(record);
                const formData = {
                  ...record,
                  hire_date: record.hire_date ? dayjs(record.hire_date) : null
                };
                form.setFieldsValue(formData);
                setSalaryType(record.salary_type || 'hourly');
                setIsModalVisible(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this employee?"
            description="This permanently removes the employee record."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Tooltip title="Delete employee">
              <Button type="text" className="icon-action" data-tone="danger" aria-label="Delete employee" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    }
  ];

  const filteredEmployees = employees.filter(employee => {
    const searchLower = searchText.toLowerCase();
    const fullName = `${employee.first_name} ${employee.last_name}`.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      employee.fingerprint_id?.toLowerCase().includes(searchLower) ||
      employee.employee_number?.toLowerCase().includes(searchLower) ||
      employee.position?.toLowerCase().includes(searchLower) ||
      employee.department?.toLowerCase().includes(searchLower)
    );
  });

  const activeCount = employees.filter(e => e.status === 'active').length;

  return (
    <Page>
      <PageHeader
        title="Employees"
        subtitle={`${employees.length} employees · ${activeCount} active`}
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
          >
            Add employee
          </Button>
        }
      />

      <Card className="table-card">
        <div className="toolbar" style={{ padding: '12px 8px 8px' }}>
          <Input
            placeholder="Search by name, ID, NIC, department or position"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            style={{ width: 380, maxWidth: '100%' }}
          />
          <span className="toolbar__spacer" />
          {searchText && (
            <span className="text-muted">{filteredEmployees.length} of {employees.length} shown</span>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={filteredEmployees}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} employees`
          }}
        />
      </Card>

      {/* MODAL */}
      <Modal
        title={
          <Space>
            <UserOutlined />
            {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
          </Space>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingEmployee(null);
          form.resetFields();
        }}
        footer={null}
        width={900}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddEdit}
          initialValues={{
            status: 'active',
            salary_type: 'hourly',
            hire_date: dayjs(),
            break_minutes: 60
          }}
        >
          {/* SECTION 1: Basic Info */}
          <Card title={<Space><UserOutlined /> Basic Information</Space>} size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="fingerprint_id"
                  label="Employee ID"
                  rules={[{ validator: validateEmployeeId }]}
                >
                  <Input placeholder="Fingerprint ID" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="employee_number"
                  label="NIC"
                  tooltip="National Identity Card Number (for payslip)"
                  rules={[{ required: true, message: 'Please input NIC' }]}
                >
                  <Input placeholder="NIC Number" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="hire_date"
                  label="Hire Date"
                  rules={[{ required: true, message: 'Please select hire date' }]}
                >
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="first_name"
                  label="First Name"
                  rules={[{ required: true, message: 'Please input first name' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="last_name"
                  label="Last Name"
                  rules={[{ required: true, message: 'Please input last name' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="phone" label="Phone">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="department"
                  label="Department"
                  rules={[{ required: true, message: 'Please select department' }]}
                >
                  <Select placeholder="Select department" showSearch>
                    {departments.map(dept => (
                      <Select.Option key={dept.id} value={dept.name}>
                        {dept.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="position"
                  label="Position"
                  rules={[{ required: true, message: 'Please input position' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* SECTION 2: Salary & Break */}
          <Card 
            title={<Space><DollarOutlined /> Salary & Working Hours</Space>} 
            size="small" 
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="salary_type"
                  label="Salary Type"
                  rules={[{ required: true, message: 'Please select salary type' }]}
                >
                  <Select onChange={handleSalaryTypeChange}>
                    <Select.Option value="hourly">Hourly</Select.Option>
                    <Select.Option value="weekly">Weekly</Select.Option>
                    <Select.Option value="monthly">Monthly</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={8}>
                {salaryType === 'hourly' && (
                  <Form.Item
                    name="hourly_rate"
                    label="Hourly Rate (Rs)"
                    rules={[{ required: true, message: 'Please input hourly rate' }]}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
                  </Form.Item>
                )}
                {salaryType === 'weekly' && (
                  <Form.Item
                    name="weekly_salary"
                    label="Weekly Salary (Rs)"
                    rules={[{ required: true, message: 'Please input weekly salary' }]}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
                  </Form.Item>
                )}
                {salaryType === 'monthly' && (
                  <Form.Item
                    name="monthly_salary"
                    label="Monthly Salary (Rs)"
                    rules={[{ required: true, message: 'Please input monthly salary' }]}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
                  </Form.Item>
                )}
              </Col>

              <Col span={8}>
                <Form.Item
                  name="break_minutes"
                  label={
                    <Space>
                      <ClockCircleOutlined />
                      Break Time (minutes)
                    </Space>
                  }
                  tooltip="Daily break duration deducted from working hours (e.g., 10, 30, 60 minutes)"
                  rules={[
                    { required: true, message: 'Please input break minutes' },
                    { type: 'number', min: 0, max: 480, message: 'Must be between 0-480 minutes' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={480}
                    step={10}
                    placeholder="60"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="monthly_overtime_threshold"
                  label={
                    <Space>
                      <ClockCircleOutlined />
                      Monthly OT Threshold (hours)
                    </Space>
                  }
                  tooltip="Employee-specific monthly overtime threshold. Leave empty to use company default from Settings → Payroll (195 hours)"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={0.5}
                    placeholder="Leave empty for company default (195h)"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <div className="soft-panel text-muted" style={{ fontSize: '12.5px', lineHeight: 1.6 }}>
                  <strong>Note:</strong> This is the monthly hours threshold before overtime applies.<br />
                  Example: 195h = 8h/day × 26 working days. Hours beyond this count as overtime.<br />
                  Leave empty to use the company default setting.
                </div>
              </Col>
            </Row>
          </Card>

          {/* SECTION 3: Status */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Form.Item
              name="status"
              label="Employment Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select>
                <Select.Option value="active">Active</Select.Option>
                <Select.Option value="inactive">Inactive</Select.Option>
                <Select.Option value="terminated">Terminated</Select.Option>
              </Select>
            </Form.Item>
          </Card>

          {/* Form Actions */}
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsModalVisible(false);
                setEditingEmployee(null);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingEmployee ? 'Update Employee' : 'Add Employee'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Page>
  );
};

export default Employees;